import type {
  ApproveLeaveRequestDTO,
  CreateLeaveEntitlementDTO,
  CreateLeaveRequestDTO,
  CreateLeaveTypeDTO,
  LeaveEntitlementDetailDTO,
  LeaveRequestListItem,
  LeaveTypeDetailDTO,
  ListLeaveEntitlementsQuery,
  ListLeaveEntitlementsResult,
  ListLeaveRequestsQuery,
  ListLeaveRequestsResult,
  ListLeaveTypesQuery,
  ListLeaveTypesResult,
  UpdateLeaveEntitlementDTO,
  UpdateLeaveRequestDTO,
  UpdateLeaveTypeDTO,
} from "@/modules/hr/leave/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/hr/leave`;

export const leaveApi = {
  listTypes(query: ListLeaveTypesQuery): Promise<ListLeaveTypesResult> {
    const params = new URLSearchParams();
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.sort != null) params.set("sort", JSON.stringify(query.sort));
    if (query.isActive != null) params.set("isActive", String(query.isActive));
    if (query.search != null) params.set("search", query.search);
    const q = params.toString();
    return fetcher.get<ListLeaveTypesResult>(
      q ? `${base}/types?${q}` : `${base}/types`,
    );
  },

  getTypeById(id: string): Promise<LeaveTypeDetailDTO> {
    return fetcher.get<LeaveTypeDetailDTO>(`${base}/types/${id}`);
  },

  createType(dto: CreateLeaveTypeDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${base}/types`, dto);
  },

  updateType(id: string, dto: UpdateLeaveTypeDTO): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/types/${id}`, dto);
  },

  deleteType(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(`${base}/types/${id}`);
  },

  listEntitlements(
    query: ListLeaveEntitlementsQuery,
  ): Promise<ListLeaveEntitlementsResult> {
    const params = new URLSearchParams();
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.year != null) params.set("year", String(query.year));
    if (query.employeeId != null) params.set("employeeId", query.employeeId);
    if (query.leaveTypeId != null) params.set("leaveTypeId", query.leaveTypeId);
    if (query.search != null) params.set("search", query.search);
    const q = params.toString();
    return fetcher.get<ListLeaveEntitlementsResult>(
      q ? `${base}/entitlements?${q}` : `${base}/entitlements`,
    );
  },

  getEntitlementById(id: string): Promise<LeaveEntitlementDetailDTO> {
    return fetcher.get<LeaveEntitlementDetailDTO>(`${base}/entitlements/${id}`);
  },

  createEntitlement(dto: CreateLeaveEntitlementDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${base}/entitlements`, dto);
  },

  updateEntitlement(
    id: string,
    dto: UpdateLeaveEntitlementDTO,
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/entitlements/${id}`, dto);
  },

  listRequests(
    query: ListLeaveRequestsQuery,
  ): Promise<ListLeaveRequestsResult> {
    const params = new URLSearchParams();
    if (query.offset != null) params.set("offset", String(query.offset));
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.sort != null) params.set("sort", JSON.stringify(query.sort));
    if (query.status != null) params.set("status", query.status);
    if (query.employeeId != null) params.set("employeeId", query.employeeId);
    if (query.periodYear != null)
      params.set("periodYear", String(query.periodYear));
    if (query.periodMonth != null)
      params.set("periodMonth", String(query.periodMonth));
    if (query.periodFromOnly === true) params.set("periodFromOnly", "true");
    if (query.createdYear != null)
      params.set("createdYear", String(query.createdYear));
    if (query.createdMonth != null)
      params.set("createdMonth", String(query.createdMonth));
    if (query.createdFromOnly === true) params.set("createdFromOnly", "true");
    if (query.search != null) params.set("search", query.search);
    const q = params.toString();
    return fetcher.get<ListLeaveRequestsResult>(
      q ? `${base}/requests?${q}` : `${base}/requests`,
    );
  },

  getRequestById(id: string): Promise<LeaveRequestListItem> {
    return fetcher.get<LeaveRequestListItem>(`${base}/requests/${id}`);
  },

  approveRequest(
    id: string,
    body?: ApproveLeaveRequestDTO,
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(
      `${base}/requests/${id}/approve`,
      body ?? {},
    );
  },

  approveRequestLine(
    id: string,
    body?: ApproveLeaveRequestDTO,
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(
      `${base}/requests/${id}/approve-line`,
      body ?? {},
    );
  },

  rejectRequest(
    id: string,
    body?: { rejectionReason?: string | null },
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(
      `${base}/requests/${id}/reject`,
      body ?? {},
    );
  },

  deleteRequest(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(`${base}/requests/${id}`);
  },

  bulkApprove(ids: string[]): Promise<{ approved: number }> {
    return fetcher.post<{ approved: number }>(`${base}/requests/bulk-approve`, {
      ids,
    });
  },

  bulkReject(
    ids: string[],
    rejectionReason?: string | null,
  ): Promise<{ rejected: number }> {
    return fetcher.post<{ rejected: number }>(`${base}/requests/bulk-reject`, {
      ids,
      rejectionReason: rejectionReason ?? undefined,
    });
  },

  bulkDelete(ids: string[]): Promise<{ deleted: number }> {
    return fetcher.post<{ deleted: number }>(`${base}/requests/bulk-delete`, {
      ids,
    });
  },

  createRequest(dto: CreateLeaveRequestDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${base}/requests`, dto);
  },

  updateRequest(
    id: string,
    dto: UpdateLeaveRequestDTO,
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/requests/${id}`, dto);
  },

  listMyEntitlements(year: number): Promise<ListLeaveEntitlementsResult> {
    return fetcher.get<ListLeaveEntitlementsResult>(
      `${base}/entitlements/mine?year=${year}`,
    );
  },
};
